import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBuildPageHeader,
  MiButton,
  MiContentDrawer,
  MiIcon,
  MiPlaceholderIcon,
} from "@miplatform/components";
import {
  APP_BAR_HEIGHT,
  APP_BAR_HEIGHT_TABLET,
  BUTTON_VARIANTS,
  CONTACT_CARD_EDIT_MODES,
  CONTACT_TYPES,
  ICONS_WEB,
  MIBUILD_PAGE_HEADER_HEIGHT,
} from "@miplatform/constants";
import { ClientFilter, debounce, externalReferenceId } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { ContactsListSection, MiContactCard } from "@mibuildweb/components";
import { useContactGroups } from "@mibuildweb/utils";
import { Box, useMediaQuery } from "@mui/material";
import { AddEditContactRelationForm } from "../../CommunityDetails/AddEditContactRelationDrawer/AddEditContactRelationForm";

export function HomeContactsDrawer({
  home = {},
  contacts,
  reload,
  open,
  onClose = () => null,
}) {
  const { communityId, referenceId } = home;
  const { classes, theme } = useStyles();
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [isAddingContact, setIsAddingContact] = useState(false);
  const [filteredData, setFilteredData] = useState(contacts);
  const [selectedContact, setSelectedContact] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch("");
  }, [isAddingContact]);

  const referenceIds = useMemo(
    () => ({
      [CONTACT_TYPES.COMMUNITIES]: externalReferenceId.getCommunityRefId({
        communityId,
      }),
      [CONTACT_TYPES.HOMES]: referenceId,
    }),
    [communityId, referenceId]
  );

  const { groupedContactsByLevel } =
    useContactGroups(contacts, filteredData) || {};

  const clientFilterManager = useMemo(() => {
    const filter = new ClientFilter(
      contacts,
      ["firstName lastName", "title"],
      setFilteredData
    );
    filter.handleSort("isPrimary", "desc");
    filter.handleSearch(search);

    return filter;
  }, [contacts]);

  const handleSearch = (value) => {
    setSearch(value);
    debounce(clientFilterManager.handleSearch(value));
  };

  const handleClose = () => {
    onClose();
    setSearch("");
    setIsAddingContact(false);
    setFilteredData(contacts);
  };

  const renderContactCard = (contact) => (
    <Box key={`${contact.id}${contact.level}${contact.label}`}>
      <MiContactCard
        isPrimaryDisabled
        enableDeleteRelationship={contact.level === CONTACT_TYPES.HOMES}
        contact={contact}
        accountId={contact.accountId}
        referenceIds={referenceIds}
        editMode={CONTACT_CARD_EDIT_MODES.CONTACT}
        onRefresh={reload}
        defaultContactLevel={CONTACT_TYPES.HOMES}
      />
    </Box>
  );

  const getLevelGroupHeader = (level) => {
    switch (level) {
      case CONTACT_TYPES.COMMUNITIES:
        return "Community";
      case CONTACT_TYPES.HOMES:
        return "Home";
      default:
        return "Contacts";
    }
  };

  const title = !isAddingContact ? "All Contacts" : "Add Contact";

  const header = (
    <MiBuildPageHeader
      title={title}
      titleVariant="h6"
      titleColor="primary.main"
      titleFontWeight={theme.fontWeight.semiBold}
      titleIcon={
        <Box
          backgroundColor={theme.palette.primary.light}
          borderRadius={2}
          padding={1}
          width={28}
          height={28}
        >
          <MiIcon
            path={mdi[ICONS_WEB.ACCOUNT_OUTLINE]}
            size={0.8}
            color={theme.palette.primary.dark}
            data-testid="left-icon"
          />
        </Box>
      }
      hasDivider
      style={{ minHeight: 48 }}
      search={search}
      searchPlaceholder="Find Contact"
      onHandleSearch={
        !isAddingContact ? (e) => handleSearch(e.target.value) : undefined
      }
      actionComponent={
        !isAddingContact ? (
          <Box
            display="flex"
            alignItems="center"
            gap={3}
            className={classes.headerActions}
          >
            <MiButton
              title="Add Contact"
              startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
              sx={{ height: 34 }}
              variant={BUTTON_VARIANTS.OUTLINED}
              onClick={() => setIsAddingContact(true)}
              data-testid="add-contact-btn"
            />
            <MiButton
              startIcon={{
                path: mdi[ICONS_WEB.CLOSE],
                iconColor: theme.palette.grey.main,
              }}
              onClick={handleClose}
              data-testid="right-icon-button"
              style={{ padding: theme.spacing(1) }}
            />
          </Box>
        ) : null
      }
    />
  );

  const DRAWER_TOP_OFFSET =
    (isTablet ? APP_BAR_HEIGHT_TABLET : APP_BAR_HEIGHT) +
    MIBUILD_PAGE_HEADER_HEIGHT;

  return (
    <MiContentDrawer
      variant="persistent"
      open={open}
      onClose={handleClose}
      Header={header}
      slotProps={{
        transition: {
          unmountOnExit: true,
        },
      }}
      hideBackdrop
      sx={{
        "& > .MuiPaper-root": {
          top: DRAWER_TOP_OFFSET,
          height: `calc(100% - ${DRAWER_TOP_OFFSET}px)`,
        },
      }}
    >
      <Box className={classes.viewContainer} height="inherit">
        {!isAddingContact ? (
          <Box className={classes.content}>
            {groupedContactsByLevel &&
            Object.keys(groupedContactsByLevel).length !== 0 ? (
              <>
                {groupedContactsByLevel[CONTACT_TYPES.HOMES]?.length && (
                  <ContactsListSection
                    header={getLevelGroupHeader(CONTACT_TYPES.HOMES)}
                    items={groupedContactsByLevel[CONTACT_TYPES.HOMES]}
                    renderCard={renderContactCard}
                  />
                )}
                {groupedContactsByLevel[CONTACT_TYPES.COMMUNITIES]?.length && (
                  <ContactsListSection
                    header={getLevelGroupHeader(CONTACT_TYPES.COMMUNITIES)}
                    items={groupedContactsByLevel[CONTACT_TYPES.COMMUNITIES]}
                    renderCard={renderContactCard}
                  />
                )}
              </>
            ) : (
              <Box alignSelf="center">
                <MiPlaceholderIcon
                  icon={mdi[ICONS_WEB.ACCOUNT]}
                  text="No Contacts Found"
                  helperText={
                    !search
                      ? "Click Add Contact to add or create a new contact to this home"
                      : "Please adjust your search criteria and try again"
                  }
                />
              </Box>
            )}
          </Box>
        ) : (
          <AddEditContactRelationForm
            selectedContact={selectedContact}
            defaultContactLevel={CONTACT_TYPES.HOMES}
            referenceIds={referenceIds}
            open={isAddingContact}
            onClose={() => {
              setIsAddingContact(false);
              setSelectedContact(null);
            }}
            onRefresh={reload}
          />
        )}
      </Box>
    </MiContentDrawer>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    backgroundColor: theme.palette.grey.accent,
    overflow: "hidden",
  },
  leftSection: {
    flex: 0.15,
    margin: "8px 0px 8px 8px",
    minWidth: 150,
  },
  viewContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100%",
  },
  content: {
    display: "grid",
    gap: theme.spacing(4),
    overflow: "auto",
    height: "100%",
  },
}));
